import type { StackFrame } from './types.js';

// Chrome/V8:   "  at functionName (filename:line:col)"
//              "  at filename:line:col"
const CHROME_RE = /^\s*at (?:(.*?) \()?(.+?):(\d+):(\d+)\)?$/;

// Firefox/Safari: "functionName@filename:line:col"
//                 "@filename:line:col"
const FIREFOX_RE = /^(?:(.+?)@)?(.+?):(\d+):(\d+)$/;

// Origins that are not "in-app" frames
const INTERNAL_PATTERNS = [/node_modules\//, /bugcatch[-_]?sdk/, /^\(native\)$/, /^native code/];

function isInApp(filename: string | undefined): boolean {
  if (!filename) return false;
  return !INTERNAL_PATTERNS.some((p) => p.test(filename));
}

function parseChromeLine(line: string): StackFrame | null {
  const m = CHROME_RE.exec(line);
  if (!m) return null;
  const [, fn, filename, lineno, colno] = m;
  return {
    function: fn ?? '<anonymous>',
    filename: filename ?? undefined,
    lineno: lineno ? parseInt(lineno, 10) : undefined,
    colno: colno ? parseInt(colno, 10) : undefined,
    in_app: isInApp(filename ?? undefined),
  };
}

function parseFirefoxLine(line: string): StackFrame | null {
  const m = FIREFOX_RE.exec(line);
  if (!m) return null;
  const [, fn, filename, lineno, colno] = m;
  return {
    function: fn ?? '<anonymous>',
    filename: filename ?? undefined,
    lineno: lineno ? parseInt(lineno, 10) : undefined,
    colno: colno ? parseInt(colno, 10) : undefined,
    in_app: isInApp(filename ?? undefined),
  };
}

export function parseStack(error: Error): StackFrame[] {
  const stack = error.stack;
  if (!stack) return [];

  const lines = stack.split('\n');
  const frames: StackFrame[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('Error') || trimmed.startsWith(error.message ?? '')) {
      continue;
    }
    const frame = parseChromeLine(trimmed) ?? parseFirefoxLine(trimmed);
    if (frame) {
      frames.push(frame);
    }
  }

  // The API fingerprinting uses the LAST in-app frame, which maps to innermost
  // call. Return in innermost-first order (as captured).
  return frames;
}
